import { useAdmCapabilities } from "../admCountryCapabilities";
import React, { useEffect, useRef, useState } from "react";
import { generatePath, useHistory } from "react-router";
import cn from "classnames";
import { Image, Upload } from "lucide-react";
import ROUTES from "config/routing";
import { authorization } from "module/auth";
import { Card, CardTitle } from "campaign-builder/ui";
import useDesignManagement, { type Design } from "pages/creative/pageV2/useDesignManagement";
import { updateCampaignExtraData } from "graphQL/mutations/campaignExtraData";
import type { BuilderStepProps } from "campaign-builder/config";
import { admChannelConfig } from "campaign-builder/channels/directmail/adm/adm.config";
import { ADM_FORMAT } from "campaign-builder/domain/constants";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useCampaignCostPreviewCalculation } from "campaign-builder/hooks/useCampaignCostPreviewCalculation";
import { useTargetingValidation } from "../targetingValidation";
import { useDesignSelection } from "../designSelection";
import { SelectedFormatRow } from "./SelectedFormatRow";
import { DesignGalleryGrid } from "./DesignGalleryGrid";
import { UploadPane } from "./UploadPane";
import { CreatePane } from "./CreatePane";
import { FieldError } from "../targeting/fields";
import { getAdmFormatMeta } from "./formatMeta";
import { WandSparkleIcon } from "./icons";
import { AddressBoxRequiredModal } from "./AddressBoxRequiredModal";
import { FormatChangeModal } from "./FormatChangeModal";
import { InvalidationModal, doesChangeImpactTracking, type TrackingImpactVariant } from "campaign-builder/invalidation";
import { useTrackingInvalidation } from "../useTrackingInvalidation";
import type { DesignView } from "./types";
import { builderFormat } from "campaign-builder/formats/builderFormat";
import { buildAddressBoxSearch, getAddressBoxEntry } from "pages/creative/pageV2/utils/builderParams";
import { useFigmaAddressBoxDialog } from "pages/creative/pageV2/utils/useFigmaAddressBoxDialog";
import { seedImportOrientation } from "pages/creative/pageV2/utils/addressBoxOrientation";
import { addressBoxTarget, lacksAddressBox, planDesignPick } from "./addressBoxGate";

type Source = "gallery" | "upload" | "create";

const SOURCES: { id: Source; label: string; icon: React.ReactNode; dataCy: string }[] = [
  {
    id: "gallery",
    label: "Select from gallery",
    icon: <Image className="h-[18px] w-[18px]" />,
    dataCy: "admDesignSelectFromGalleryButton",
  },
  {
    id: "upload",
    label: "Upload design",
    icon: <Upload className="h-[18px] w-[18px]" />,
    dataCy: "admDesignUploadButton",
  },
  {
    id: "create",
    label: "Create new",
    icon: <WandSparkleIcon className="h-[18px] w-[18px]" />,
    dataCy: "admDesignCreateNewButton",
  },
];

const toView = (design: Design): DesignView => ({
  id: design.id,
  designName: design.designName,
  format: design.format,
  thumbnailUrl: design.thumbnailUrl,
  s3Link: design.s3Link,
  addressBox: design.addressBox,
  isUploaded: design.isUploaded,
  hasHtmlContent: design.hasHtmlContent,
  designSource: design.designSource,
  hasFigmaPlaceholders: design.hasFigmaPlaceholders,
  usedByCampaign: design.usedByCampaign,
});

export const AdmDesignStep: React.FC<BuilderStepProps> = () => {
  const designConfig = admChannelConfig.design;
  const { campaignData, clientId, campaignId, refetchCampaign } = useCampaignData();
  const admCapabilities = useAdmCapabilities();
  const allowedFormats = admChannelConfig.getFormats?.(admCapabilities.countryCode).map((format) => format.value) ?? [];
  const addressBoxRequired = !!designConfig.addressBoxRequired && admCapabilities.addressBoxRequired;
  // Creative Studio designs feed the US variable-PDF pipeline, so outside the US authoring is upload-only.
  const availableSources = admCapabilities.canCreateInStudio ? SOURCES : SOURCES.filter((s) => s.id !== "create");
  const { selectedDesign, setSelectedDesign } = useDesignSelection();
  const { activateDesign, createDesign } = useDesignManagement({ clientId: clientId || "" });
  const { hasPlaceholderTracking, resetTracking } = useTrackingInvalidation();
  const history = useHistory();

  const flyerType = campaignData?.flyerType ?? "";
  const formatMeta = getAdmFormatMeta(flyerType);

  const [source, setSource] = useState<Source>("gallery");
  const [addressBoxOpen, setAddressBoxOpen] = useState(false);
  // PAD-32395: a picked design with no address box waits here for the popup choice, unselected and not activated.
  const [heldDesign, setHeldDesign] = useState<DesignView | null>(null);
  const [selectError, setSelectError] = useState<string | null>(null);
  const [pendingChange, setPendingChange] = useState<{ format: string; design?: Design } | null>(null);
  const [invalidation, setInvalidation] = useState<{ variant: TrackingImpactVariant; apply: () => void } | null>(null);
  const [openingStudio, setOpeningStudio] = useState(false);

  const { campaignCostPreview: liveCost } = useCampaignCostPreviewCalculation();
  const { campaignCostPreview: previewCost } = useCampaignCostPreviewCalculation({
    format: pendingChange?.format as ADM_FORMAT | undefined,
    dryRun: true,
  });

  // When the user clicks the always-on CTA and the design step is incomplete
  // because the selected design has no address box, surface the address-box modal.
  const { version: validationVersion } = useTargetingValidation();
  const lastValidation = useRef(0);
  useEffect(() => {
    if (validationVersion === lastValidation.current) return;
    lastValidation.current = validationVersion;
    if (!selectedDesign) {
      setSelectError("Select a design to continue.");
      return;
    }
    if (lacksAddressBox(addressBoxRequired, selectedDesign)) {
      setAddressBoxOpen(true);
    }
  }, [validationVersion, selectedDesign, addressBoxRequired]);

  const isFormatAllowed = (format: string) => allowedFormats.includes(format);

  const persistFormat = async (format: string) => {
    if (!campaignId) return;
    // The format row and the gallery already filter on the allow-list; this is
    // the last gate before the write, so no path (a stale design, a builder
    // default) can store a format the campaign's country does not print.
    if (!isFormatAllowed(format)) {
      setSelectError("This format is not available in the campaign's country.");
      return;
    }
    try {
      await updateCampaignExtraData({ campaignId, flyerType: format });
      await refetchCampaign();
    } catch (err) {
      console.error("persistFormat error", err);
      setSelectError("Failed to update the format. Please try again.");
    }
  };

  const applyDesign = (design: Design) => {
    const view = toView(design);
    setSelectError(null);
    if (planDesignPick(addressBoxRequired, view).askForAddressBox) {
      setHeldDesign(view);
      setAddressBoxOpen(true);
      return;
    }
    setSelectedDesign(view);
    if (campaignId) void activateDesign(campaignId, design.id);
  };

  const selectDesign = (design: Design) => {
    // Switching the design when placeholder-based tracking is set up invalidates it.
    // We conservatively pass placeholderSetChanged:true (the incoming design's
    // placeholders aren't fetched here), so the warning may fire even if they happen
    // to match — safer than silently breaking tracking. Confirm clears the config (F5).
    if (hasPlaceholderTracking) {
      const variant = doesChangeImpactTracking({
        hasTrackingConfig: true,
        designChanged: true,
        placeholderSetChanged: true,
      });
      if (variant) {
        setInvalidation({ variant, apply: () => applyDesign(design) });
        return;
      }
    }
    applyDesign(design);
  };

  const confirmInvalidation = async () => {
    const pending = invalidation;
    setInvalidation(null);
    if (!pending) return;
    try {
      await resetTracking();
      pending.apply();
    } catch (error) {
      // reset failed — leave tracking intact and don't apply the design change
    }
  };

  const handleGallerySelect = (design: Design) => {
    // Clicking the already-selected design de-selects it.
    if (selectedDesign?.id === design.id) {
      setSelectedDesign(null);
      return;
    }
    // The grid renders such a design as non-compliant and unselectable; kept
    // here too so the selection path cannot bypass the country's catalogue.
    if (design.format && !isFormatAllowed(design.format)) {
      setSelectError("This design's format is not available in the campaign's country.");
      return;
    }
    if (design.format && design.format !== flyerType) {
      setPendingChange({ format: design.format, design });
      return;
    }
    selectDesign(design);
  };

  const handleFormatRowSelect = (format: string) => {
    // Changing the campaign format clears a selected design of a different format.
    if (selectedDesign?.format && selectedDesign.format !== format) {
      setPendingChange({ format });
      return;
    }
    void persistFormat(format);
  };

  const confirmPendingChange = async () => {
    const change = pendingChange;
    setPendingChange(null);
    if (!change) return;
    await persistFormat(change.format);
    if (change.design) {
      selectDesign(change.design);
    } else {
      // Format-row change with no new design → clear the mismatched selection.
      setSelectedDesign(null);
    }
  };

  const builderPathFor = async (designFormat?: string) => {
    // No campaign format would open the builder without one; default like goCreateStudio does.
    const format = builderFormat(designFormat, flyerType || ADM_FORMAT.POSTCARD_9x6);
    // PAD-32920: the builder reads the campaign's format (empty-state overlay, ADM address-box
    // rules), so a draft with no format yet gets the one it is about to be edited in — the same
    // write "Update to new design" makes when a design of another format is picked.
    if (!flyerType && format) await persistFormat(format);
    return generatePath(ROUTES.FLYER_BUILDER, {
      clientId: clientId as string,
      campaignId: campaignId as string,
      format,
    });
  };

  const goToBuilder = async (
    design: Pick<DesignView, "id" | "format" | "s3Link" | "isUploaded" | "hasHtmlContent">,
    extra: Record<string, string> = {}
  ) => {
    if (!clientId || !campaignId) return;
    const path = await builderPathFor(design.format);
    const params = new URLSearchParams();
    params.set("designId", design.id);
    // Only auto-import the uploaded PDF for un-edited designs. PDFImport gates on
    // `hasHtmlContent === "false"` AND clears the canvas before re-importing, so
    // sending this pair for an already-edited design (hasHtmlContent=true) would
    // wipe the user's builder edits. Mirrors useDesignPage.ts / DesignSelection.tsx.
    if (design.isUploaded && design.s3Link && !design.hasHtmlContent) {
      params.set("fileLink", design.s3Link);
      params.set("hasHtmlContent", "false");
      // The Liveblocks reconcile honours this over the design's saved orientation (#2581).
      const orientation = await seedImportOrientation(design.s3Link, design.id, `${campaignId}`);
      if (orientation) params.set("orientation", orientation);
    }
    Object.entries(extra).forEach(([key, value]) => params.set(key, value));
    history.push(`${path}?${params.toString()}`);
  };

  // The copy (or flattened original) replaces the selection, so the campaign uses the design that gets the box.
  const { openFigmaAddressBoxDialog, figmaAddressBoxDialog } = useFigmaAddressBoxDialog({
    onPrepared: (prepared, design) => {
      void (async () => {
        if (!clientId || !campaignId) return;
        try {
          if (prepared.designId === design.id) {
            // The flattened original was only held, so it is activated now, before the builder opens.
            await activateDesign(campaignId, design.id);
            setSelectedDesign(design as DesignView);
          } else {
            await activateDesign(campaignId, prepared.designId);
            setSelectedDesign({
              ...(design as DesignView),
              id: prepared.designId,
              designName: prepared.filename,
              s3Link: prepared.fileLink,
              isUploaded: true,
              hasHtmlContent: false,
              addressBox: false,
              designSource: "upload",
              hasFigmaPlaceholders: false,
              usedByCampaign: true,
            });
          }
          const path = await builderPathFor(design.format ?? undefined);
          const orientation = await seedImportOrientation(prepared.fileLink, prepared.designId, `${campaignId}`);
          history.push(`${path}${buildAddressBoxSearch(prepared.designId, prepared.fileLink, orientation)}`);
        } catch (error) {
          console.error(`[ADM] Add address box failed for design ${prepared.designId}:`, error);
          setSelectError("We couldn't open the design tool. Please try again.");
        }
      })();
    },
  });

  const addAddressBox = async (design: DesignView) => {
    // activateDesign marks a Figma design as uploaded, so the copy or flatten choice is checked first.
    if (getAddressBoxEntry(design).kind === "needsFigmaChoice") {
      void openFigmaAddressBoxDialog(design);
      return;
    }
    if (!campaignId) return;
    try {
      // A held pick becomes the campaign design only once the user chose to add the box to it.
      if (design.id !== selectedDesign?.id) {
        await activateDesign(campaignId, design.id);
        setSelectedDesign(design);
      }
    } catch (error) {
      console.error(`[ADM] Activating design ${design.id} for the address box failed:`, error);
      setSelectError("We couldn't open the design tool. Please try again.");
      return;
    }
    await goToBuilder(design, { autoAddAddressBox: "true" });
  };

  const chooseAnotherDesign = () => {
    // Nothing new was activated, so the previous selection (if any) stays as it was.
    setAddressBoxOpen(false);
    setHeldDesign(null);
  };

  const confirmAddAddressBox = () => {
    const target = addressBoxTarget({ held: heldDesign, selected: selectedDesign });
    setAddressBoxOpen(false);
    setHeldDesign(null);
    if (target) void addAddressBox(target);
  };

  const goCreateStudio = async () => {
    if (!clientId || !campaignId || openingStudio) return;
    setOpeningStudio(true);
    try {
      // Create the design first so the builder has a room to open. Without a
      // designId the builder hangs on its `!stableDesignId` gate. Mirrors the
      // working DesignSelection / platform-next flows. Navigate with
      // `newDesign=true` (no format in the path) so it opens the empty state;
      // the ADM campaign context makes the picker show ADM formats.
      const user = authorization.getUser();
      const design = await createDesign({
        filename: "New design",
        s3Link: "",
        s3CmykLink: "",
        isUploaded: false,
        format: flyerType || ADM_FORMAT.POSTCARD_9x6,
        authorId: user?.id != null ? String(user.id) : undefined,
      });
      if (!design?.id) throw new Error("Failed to create design - no ID returned");
      await activateDesign(campaignId, design.id);
      const path = generatePath(ROUTES.FLYER_BUILDER, { clientId, campaignId });
      history.push(`${path}?designId=${design.id}&newDesign=true`);
    } catch (error) {
      console.error("[ADM] Open Creative Studio failed:", error);
      setSelectError("We couldn't open the Creative Studio. Please try again.");
    } finally {
      setOpeningStudio(false);
    }
  };

  return (
    <Card className="flex flex-col gap-5 p-[25px]" data-targeting-section="design" data-copilot-step="ADM Design">
      <CardTitle>Design details</CardTitle>

      {allowedFormats.length ? (
        <SelectedFormatRow value={flyerType} allowed={allowedFormats} onSelect={handleFormatRowSelect} />
      ) : (
        <p className="text-muted-foreground text-sm leading-5" data-cy="admDesignNoFormatsNotice">
          Direct mail formats are not available for your country yet. Please contact your account manager.
        </p>
      )}

      <div className="flex flex-col gap-2">
        <p className="text-foreground text-sm font-medium leading-5">Add your design</p>
        <div className="flex gap-2">
          {availableSources.map((tab) => {
            const selected = source === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => {
                  setSource(tab.id);
                  setSelectError(null);
                }}
                data-cy={tab.dataCy}
                className={cn(
                  "flex flex-1 items-center gap-2.5 rounded-lg border-solid px-3 py-2.5 text-sm font-medium",
                  selected
                    ? "text-foreground border-[1.5px] border-[#222336] dark:border-slate-300"
                    : "text-muted-foreground border border-border"
                )}
              >
                {tab.icon}
                {tab.label}
              </button>
            );
          })}
        </div>
        <FieldError message={selectError} />
      </div>

      {source === "gallery" && clientId && (
        <DesignGalleryGrid
          clientId={clientId}
          format={flyerType}
          activeDesignId={selectedDesign?.id ?? null}
          templateUrl={formatMeta?.template}
          templateLabel={formatMeta?.label}
          allowedFormats={allowedFormats}
          onSelect={handleGallerySelect}
          onEdit={(design) => void goToBuilder(toView(design))}
        />
      )}

      {source === "upload" && (
        <UploadPane
          designConfig={designConfig}
          format={flyerType}
          formatLabel={formatMeta?.label}
          campaignId={campaignId ?? undefined}
          clientId={clientId}
          templateUrl={formatMeta?.template}
          templateLabel={formatMeta?.label}
          onCommitted={(design) => {
            setSource("gallery");
            selectDesign(design);
          }}
        />
      )}

      {source === "create" && (
        <CreatePane onOpenCreativeStudio={() => void goCreateStudio()} isOpeningCreativeStudio={openingStudio} />
      )}

      <AddressBoxRequiredModal
        open={addressBoxOpen}
        onClose={chooseAnotherDesign}
        onAddAddressBox={confirmAddAddressBox}
      />
      {figmaAddressBoxDialog}

      <FormatChangeModal
        open={!!pendingChange}
        onCancel={() => setPendingChange(null)}
        onConfirm={() => void confirmPendingChange()}
        newTotal={previewCost?.total}
        currentTotal={liveCost?.total}
      />
      <InvalidationModal
        variant={invalidation?.variant ?? null}
        onCancel={() => setInvalidation(null)}
        onConfirm={() => void confirmInvalidation()}
      />
    </Card>
  );
};
